import React, { useState, useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { Card, CardContent, Grid, TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import Cookies from 'js-cookie';
import api from '../../services/api';
import DisponibilidadeModuloSonoro from '../DisponibilidadeModuloSonoro/DisponibilidadeModuloSonoro';

const GraficoDisponibilidade = ({ idInstalacao, instalacao, moduloSonoro, onClose }) => {
    const chartRefPGM = useRef(null);
    const chartRefCentral = useRef(null);
    const chartRefZona = useRef(null);

    const temModuloSonoro = !!moduloSonoro || !!(instalacao?.equipamentos?.some(e => e.tipo_equipamento_area_tecnica === 30));
    const [tabAtiva, setTabAtiva] = useState(temModuloSonoro ? 'cameras' : 'centrais');

    const [mesAtual, setMesAtual] = useState(new Date().getMonth() + 1);
    const [anoAtual, setAnoAtual] = useState(new Date().getFullYear());

    const [dadosDisponibilidade, setDadosDisponibilidade] = useState(null);
    const [registrosGerais, setRegistrosGerais] = useState([]);
    const [zonaSelecionada, setZonaSelecionada] = useState("");
    const [optionsZona, setOptionsZona] = useState(null);

    const [porcentagemTempoArmado, setPorcentagemTempoArmado] = useState(0);
    const [porcentagemTempoDesarmado, setPorcentagemTempoDesarmado] = useState(0);
    const [porcentagemTempoAlarmado, setPorcentagemTempoAlarmado] = useState(0);
    const [porcentagemTempoDesconectado, setPorcentagemTempoDesconectado] = useState(0);
    const [porcentagemTempoIndefinido, setPorcentagemTempoIndefinido] = useState(0);

    const [porcentagemTempoPgmOnline, setPorcentagemTempoPgmOnline] = useState(0);
    const [porcentagemTempoPgmOffline, setPorcentagemTempoPgmOffline] = useState(0);
    
    const [rankingZonas, setRankingZonas] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (idInstalacao && mesAtual && anoAtual) {
            listarDadosDisponibilidade();
        }
    }, [idInstalacao, mesAtual, anoAtual]);

    async function listarDadosDisponibilidade() {
        try {
            setLoading(true);
            setError(null);
            const token = Cookies.get("token");
            const headers = { Authorization: "Bearer " + token };

            const response = await api.get(
                `/v1/protected/registrodisponibilidade/${idInstalacao}/${mesAtual}/${anoAtual}`,
                { headers }
            );

            const registrosRaw = response.data || [];

            if (registrosRaw.length === 0) {
                // Se não houver dados, zerar
                setDadosDisponibilidade(null);
                setLoading(false);
                return;
            }

            const registros = registrosRaw.map(r => ({
                ...r,
                nome_mapa: typeof r.nome_mapa === "string" ? r.nome_mapa.toLowerCase() : ""
            }));

            setRegistrosGerais(registros);
            const registrosPGM = registros.filter(r => r.nome_mapa.includes("pgm"));
            const registrosCentral = registros.filter(r => r.nome_mapa.includes("central"));

            const toDate = (v) => {
                if (!v && v !== 0) return null;
                if (v instanceof Date) return isNaN(v) ? null : v;
                if (typeof v === "number") {
                    const d = new Date(v);
                    return isNaN(d) ? null : d;
                }
                if (typeof v === "string") {
                    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(v)) {
                        const d = new Date(v.replace(" ", "T"));
                        return isNaN(d) ? null : d;
                    }
                    const d = new Date(v);
                    return isNaN(d) ? null : d;
                }
                return null;
            };

            const getDiaISO = (data) => {
                const d = toDate(data);
                return d ? d.toISOString().slice(0, 10) : null;
            };

            const N_SEGMENTS = 144;
            const yLabels = Array.from({ length: N_SEGMENTS }, (_, i) => {
                const startMinutes = i * 10;
                const endMinutes = startMinutes + 10;
                const pad = n => String(n).padStart(2, '0');
                const sh = Math.floor(startMinutes / 60), sm = startMinutes % 60;
                const eh = Math.floor(endMinutes / 60), em = endMinutes % 60;
                return `${pad(sh)}:${pad(sm)}-${pad(eh)}:${pad(em)}`;
            });

            const criarDataHeatmap = (registrosFiltrados, tipo) => {
                const diasISO = Array.from(new Set(
                    registrosFiltrados.map(r => getDiaISO(r.data_hora_registro)).filter(d => d)
                )).sort();

                const agora = new Date();
                const dataAtualISO = agora.toISOString().slice(0, 10);
                const minutoAtual = agora.getHours() * 60 + agora.getMinutes();

                const dataHeatmap = [];

                diasISO.forEach((day, xIndex) => {
                    const MINUTES_PER_DAY = 1440;
                    const bucketsMin = new Array(MINUTES_PER_DAY).fill(null);

                    registrosFiltrados.forEach(r => {
                        const d = toDate(r.data_hora_registro);
                        if (!d) return;
                        const dia = d.toISOString().slice(0, 10);
                        if (dia !== day) return;
                        const minuteIndex = d.getHours() * 60 + d.getMinutes();
                        if (minuteIndex < 0) return;
                        const idx = Math.min(Math.max(0, minuteIndex), MINUTES_PER_DAY - 1);
                        bucketsMin[idx] = r;
                    });

                    for (let seg = 0; seg < N_SEGMENTS; seg++) {
                        const segStart = seg * 10;
                        const segEnd = segStart + 10;

                        if (day === dataAtualISO && segStart > minutoAtual) {
                            dataHeatmap.push([xIndex, seg, null]);
                            continue;
                        }

                        let bucketRecord = null;
                        for (let m = Math.min(segEnd - 1, 1439); m >= segStart; m--) {
                            if (bucketsMin[m]) { bucketRecord = bucketsMin[m]; break; }
                        }

                        let valor = null;
                        if (bucketRecord) {
                            const status = bucketRecord.status_geral;
                            if (tipo === "PGM") {
                                valor = status === "pgm_online" ? 1 : 0;
                            } else {
                                switch (status) {
                                    case "armada": valor = 3; break;
                                    case "desarmada": valor = 1; break;
                                    case "alarme": valor = 0; break;
                                    case "desconectado": valor = 0; break;
                                    case "indefinido": valor = 2; break;
                                    default: valor = 1; break;
                                }
                            }
                        }
                        dataHeatmap.push([xIndex, seg, valor]);
                    }
                });

                return { diasISO, dataHeatmap };
            };

            const heatmapPGM = criarDataHeatmap(registrosPGM, "PGM");
            const heatmapCentral = criarDataHeatmap(registrosCentral, "CENTRAL");

            const optionsPGM = {
                tooltip: { formatter: (params) => { const statusMap = ["Offline", "Online"]; return statusMap[params.value[2]] || "Sem dados"; } },
                grid: { top: 40, bottom: 40, left: "10%", right: "10%" },
                xAxis: { type: "category", data: heatmapPGM.diasISO.map(d => d.split("-")[2]), name: "Dia", boundaryGap: true },
                yAxis: { type: "category", data: yLabels, inverse: true, name: "Faixas 10min" },
                visualMap: { show: false, min: 0, max: 1, inRange: { color: ["#FF0000", "#00B050"] } },
                series: [{ type: "heatmap", data: heatmapPGM.dataHeatmap, emphasis: { disabled: true }, progressive: 10000 }]
            };

            const optionsCentral = {
                tooltip: { formatter: (params) => { const statusMap = ["Alarme/Desconectado", "Desarmada", "Indefinido", "Armada"]; return statusMap[params.value[2]] || "Sem dados"; } },
                grid: { top: 40, bottom: 40, left: "10%", right: "10%" },
                xAxis: { type: "category", data: heatmapCentral.diasISO.map(d => d.split("-")[2]), name: "Dia", boundaryGap: true },
                yAxis: { type: "category", data: yLabels, inverse: true, name: "Faixas 10min" },
                visualMap: { show: false, min: 0, max: 3, inRange: { color: ["#FF0000", "#C0C0C0", "#FFA500", "#00B050"] } },
                series: [{ type: "heatmap", data: heatmapCentral.dataHeatmap, emphasis: { disabled: true }, progressive: 10000 }]
            };

            const totaisPGM = { online: 0, offline: 0 };
            registrosPGM.forEach(r => { if (r.status_geral === "pgm_online") totaisPGM.online++; else totaisPGM.offline++; });
            const totalPGM = totaisPGM.online + totaisPGM.offline || 1;
            setPorcentagemTempoPgmOnline(((totaisPGM.online / totalPGM) * 100).toFixed(1));
            setPorcentagemTempoPgmOffline(((totaisPGM.offline / totalPGM) * 100).toFixed(1));

            const totaisCentral = { armada: 0, desarmada: 0, alarme: 0, desconectado: 0, indefinido: 0 };
            registrosCentral.forEach(r => {
                switch (r.status_geral) {
                    case "armada": totaisCentral.armada++; break;
                    case "desarmada": totaisCentral.desarmada++; break;
                    case "alarme": totaisCentral.alarme++; break;
                    case "desconectado": totaisCentral.desconectado++; break;
                    case "indefinido": totaisCentral.indefinido++; break;
                    default: break;
                }
            });
            const totalCentral = Object.values(totaisCentral).reduce((a, b) => a + b, 0) || 1;
            setPorcentagemTempoArmado(((totaisCentral.armada / totalCentral) * 100).toFixed(1));
            setPorcentagemTempoDesarmado(((totaisCentral.desarmada / totalCentral) * 100).toFixed(1));
            setPorcentagemTempoAlarmado(((totaisCentral.alarme / totalCentral) * 100).toFixed(1));
            setPorcentagemTempoDesconectado(((totaisCentral.desconectado / totalCentral) * 100).toFixed(1));
            setPorcentagemTempoIndefinido(((totaisCentral.indefinido / totalCentral) * 100).toFixed(1));

            const analiseZonas = {};

            registros.forEach(r => {
                const statusZonasStr = r.status_zonas || r.statusZonas;
                if (statusZonasStr) {
                    const zonasSplit = statusZonasStr.split(';');
                    const dataRegistro = toDate(r.data_hora_registro);
                    if (!dataRegistro) return;

                    zonasSplit.forEach(z => {
                        if (!z) return;
                        const partes = z.split(',').map(p => p.trim());
                        if (partes.length >= 3) {
                            const id = partes[0];
                            const nome = partes[1];
                            const zoneStatus = (partes[2] || '').toLowerCase().trim(); // online/offline
                            const armeStatus = (partes[3] || '').toLowerCase().trim(); // armada/desarmada
                            const alarmado = (partes[4] || '').toLowerCase().trim();  // true/false

                            if (!analiseZonas[id]) {
                                analiseZonas[id] = { id, nome, armada: 0, desarmada: 0, offline: 0, alarmada: 0, alarmesRaw: [] };
                            }

                            const isOffline = zoneStatus === 'false' || zoneStatus === '0' || zoneStatus === 'offline';
                            const isAlarmada = alarmado === 'true' || alarmado === '1';
                            const isArmada = armeStatus === 'armada' || armeStatus === 'true' || armeStatus === '1';

                            if (isAlarmada) {
                                analiseZonas[id].alarmada++;
                                analiseZonas[id].alarmesRaw.push(dataRegistro);
                            } else if (isOffline) {
                                analiseZonas[id].offline++;
                            } else if (isArmada) {
                                analiseZonas[id].armada++;
                            } else {
                                analiseZonas[id].desarmada++;
                            }
                        }
                    });
                }
            });

            const zonasProcessadas = Object.values(analiseZonas).map(zona => {
                zona.alarmesRaw.sort((a, b) => a - b);
                const periodos = [];
                let periodoAtual = null;

                zona.alarmesRaw.forEach(d => {
                    if (!periodoAtual) {
                        periodoAtual = { inicio: d, fim: d };
                        periodos.push(periodoAtual);
                    } else {
                        const diffMinutos = (d - periodoAtual.fim) / (1000 * 60);
                        if (diffMinutos <= 40) { 
                            periodoAtual.fim = d;
                        } else {
                            periodoAtual = { inicio: d, fim: d };
                            periodos.push(periodoAtual);
                        }
                    }
                });
                
                return {
                    id: zona.id,
                    nome: zona.nome,
                    armada: zona.armada,
                    desarmada: zona.desarmada,
                    offline: zona.offline,
                    alarmada: zona.alarmada,
                    totalAlarmes: zona.alarmada,
                    periodos
                };
            });

            zonasProcessadas.sort((a, b) => b.alarmada - a.alarmada);
            setRankingZonas(zonasProcessadas);
            if (zonasProcessadas.length > 0) {
                setZonaSelecionada(prev => prev ? prev : zonasProcessadas[0].id);
            }

            setDadosDisponibilidade({ PGM: optionsPGM, CENTRAL: optionsCentral });

        } catch (_err) {
            setError("Erro ao listar dados de disponibilidade!");
            console.error("Erro ao listar informações: ", _err);
        } finally {
            setLoading(false);
        }
    }

    const toDateUtils = (v) => {
        if (!v && v !== 0) return null;
        if (v instanceof Date) return isNaN(v) ? null : v;
        if (typeof v === "number") {
            const d = new Date(v);
            return isNaN(d) ? null : d;
        }
        if (typeof v === "string") {
            if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(v)) {
                const d = new Date(v.replace(" ", "T"));
                return isNaN(d) ? null : d;
            }
            const d = new Date(v);
            return isNaN(d) ? null : d;
        }
        return null;
    };

    const getDiaISOUtils = (data) => {
        const d = toDateUtils(data);
        return d ? d.toISOString().slice(0, 10) : null;
    };

    useEffect(() => {
        if (!zonaSelecionada || registrosGerais.length === 0) return;

        const diasISO = Array.from(new Set(
            registrosGerais.map(r => getDiaISOUtils(r.data_hora_registro)).filter(d => d)
        )).sort();

        const agora = new Date();
        const dataAtualISO = agora.toISOString().slice(0, 10);
        const minutoAtual = agora.getHours() * 60 + agora.getMinutes();

        const dataHeatmap = [];
        const N_SEGMENTS = 144;
        const yLabels = Array.from({ length: N_SEGMENTS }, (_, i) => {
            const startMinutes = i * 10;
            const endMinutes = startMinutes + 10;
            const pad = n => String(n).padStart(2, '0');
            const sh = Math.floor(startMinutes / 60), sm = startMinutes % 60;
            const eh = Math.floor(endMinutes / 60), em = endMinutes % 60;
            return `${pad(sh)}:${pad(sm)}-${pad(eh)}:${pad(em)}`;
        });

        diasISO.forEach((day, xIndex) => {
            const MINUTES_PER_DAY = 1440;
            const bucketsMin = new Array(MINUTES_PER_DAY).fill(null);

            registrosGerais.forEach(r => {
                const d = toDateUtils(r.data_hora_registro);
                if (!d) return;
                const dia = d.toISOString().slice(0, 10);
                if (dia !== day) return;
                const minuteIndex = d.getHours() * 60 + d.getMinutes();
                if (minuteIndex < 0) return;
                const idx = Math.min(Math.max(0, minuteIndex), MINUTES_PER_DAY - 1);
                bucketsMin[idx] = r;
            });

            for (let seg = 0; seg < N_SEGMENTS; seg++) {
                const segStart = seg * 10;
                const segEnd = segStart + 10;

                if (day === dataAtualISO && segStart > minutoAtual) {
                    dataHeatmap.push([xIndex, seg, null]);
                    continue;
                }

                let bucketRecord = null;
                for (let m = Math.min(segEnd - 1, 1439); m >= segStart; m--) {
                    if (bucketsMin[m]) { bucketRecord = bucketsMin[m]; break; }
                }

                let valor = null;
                if (bucketRecord) {
                    const statusZonasStr = bucketRecord.status_zonas || bucketRecord.statusZonas;
                    if (statusZonasStr) {
                        const zonasSplit = statusZonasStr.split(';');
                        const z = zonasSplit.find(zone => zone.startsWith(`${zonaSelecionada},`));
                        if (z) {
                            const partes = z.split(',').map(p => p.trim());
                            const zoneStatus = (partes[2] || '').toLowerCase().trim();
                            const armeStatus = (partes[3] || '').toLowerCase().trim();
                            const alarmado = (partes[4] || '').toLowerCase().trim();

                            const isOffline = zoneStatus === 'false' || zoneStatus === '0' || zoneStatus === 'offline';
                            const isAlarmada = alarmado === 'true' || alarmado === '1';
                            const isArmada = armeStatus === 'armada' || armeStatus === 'true' || armeStatus === '1';

                            if (isAlarmada) valor = 0;
                            else if (isOffline) valor = 2; // Offline
                            else if (isArmada) valor = 3; // Armada
                            else valor = 1; // Desarmada
                        }
                    }
                }
                dataHeatmap.push([xIndex, seg, valor]);
            }
        });

        const newOptionsZona = {
            tooltip: { formatter: (params) => { const statusMap = ["Alarmada", "Desarmada", "Offline", "Armada"]; return statusMap[params.value[2]] || "Sem dados"; } },
            grid: { top: 40, bottom: 40, left: "10%", right: "10%" },
            xAxis: { type: "category", data: diasISO.map(d => d.split("-")[2]), name: "Dia", boundaryGap: true },
            yAxis: { type: "category", data: yLabels, inverse: true, name: "Faixas 10min" },
            visualMap: { show: false, min: 0, max: 3, inRange: { color: ["#FF0000", "#C0C0C0", "#FF4500", "#00B050"] } },
            series: [{ type: "heatmap", data: dataHeatmap, emphasis: { disabled: true }, progressive: 10000 }]
        };
        setOptionsZona(newOptionsZona);
    }, [zonaSelecionada, registrosGerais]);

    useEffect(() => {
        let chartPGM, chartCentral, chartZona;
        if (dadosDisponibilidade && !loading && !error) {
            if (chartRefPGM.current) {
                chartPGM = echarts.init(chartRefPGM.current);
                chartPGM.setOption(dadosDisponibilidade.PGM);
            }
            if (chartRefCentral.current) {
                chartCentral = echarts.init(chartRefCentral.current);
                chartCentral.setOption(dadosDisponibilidade.CENTRAL);
            }
        }
        if (optionsZona && chartRefZona.current) {
            chartZona = echarts.init(chartRefZona.current);
            chartZona.setOption(optionsZona);
        }
        return () => {
            if (chartPGM) chartPGM.dispose();
            if (chartCentral) chartCentral.dispose();
            if (chartZona) chartZona.dispose();
        };
    }, [dadosDisponibilidade, loading, error, optionsZona]);

    if (tabAtiva === 'cameras') {
        return (
            <div>
                <div style={{
                    position: 'fixed',
                    bottom: '24px',
                    right: '24px',
                    zIndex: 100001,
                    display: 'flex',
                    gap: '8px'
                }}>
                    <button
                        onClick={() => setTabAtiva('centrais')}
                        style={{
                            backgroundColor: '#ffffff',
                            color: '#1976d2',
                            border: '1px solid #1976d2',
                            padding: '8px 18px',
                            borderRadius: '20px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            fontSize: '13px'
                        }}
                    >
                        📊 Alternar para Gráficos Central / PGM
                    </button>
                </div>
                <DisponibilidadeModuloSonoro
                    idInstalacao={idInstalacao}
                    moduloSonoro={moduloSonoro}
                    onClose={onClose}
                />
            </div>
        );
    }

    return (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0, 0, 0, 0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 99999 }}>
            <div style={{ backgroundColor: "white", width: "95%", height: "95%", borderRadius: "8px", boxShadow: "0 4px 10px rgba(0, 0, 0, 0.3)", display: "flex", flexDirection: "column", padding: "10px", position: "relative", overflowY: "auto" }}>
                <button onClick={onClose} style={{ position: "absolute", top: "10px", right: "10px", backgroundColor: "#FF4500", color: "white", border: "none", borderRadius: "4px", padding: "8px", cursor: "pointer", fontWeight: "bold" }}>Fechar</button>

                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                    <button
                        onClick={() => setTabAtiva('cameras')}
                        style={{
                            backgroundColor: '#1976d2',
                            color: '#ffffff',
                            border: 'none',
                            padding: '8px 22px',
                            borderRadius: '20px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                            fontSize: '13px'
                        }}
                    >
                        📷 Disponibilidade das Câmeras (Módulo Sonoro)
                    </button>
                </div>

                <Grid item xs={12} style={{ padding: 10 }} direction={'row'} justifyContent={'center'} alignItens={'center'}>
                    <Card style={{ height: '100px', padding: 5, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', textAlign: 'center', boxShadow: '0px 4px 8px rgba(0,0,0,0.2)', backgroundColor: '#1976d2' }}>
                        <CardContent style={{ padding: 8 }}>
                            <TextField variant="standard" value={mesAtual} onChange={(e) => setMesAtual(e.target.value)} inputProps={{ style: { textAlign: 'center', fontSize: '2rem', fontWeight: 'bold', color: '#fff', width: '70px' }, maxLength: 2 }} InputProps={{ disableUnderline: true }} />
                            <TextField variant="standard" value={anoAtual} onChange={(e) => setAnoAtual(e.target.value)} inputProps={{ style: { textAlign: 'center', fontSize: '1rem', color: '#e0e0e0', letterSpacing: '1px', width: '80px' }, maxLength: 4 }} InputProps={{ disableUnderline: true }} />
                        </CardContent>
                    </Card>
                </Grid>

                <h6 style={{ marginTop: 0, textAlign: "center" }}>Disponibilidade</h6>

                {loading ? (
                    <div style={{ textAlign: "center", marginTop: "50px" }}>Carregando dados de disponibilidade...</div>
                ) : error ? (
                    <div style={{ textAlign: "center", marginTop: "50px", color: "red" }}>{error}</div>
                ) : dadosDisponibilidade ? (
                    <>
                        <div style={{ width: "90%", height: "80vh", margin: "20px auto", display: "flex", flexDirection: "column", alignItems: "center", border: "1px solid #ddd", borderRadius: "8px", padding: "10px" }}>
                            <h4>PGM</h4>
                            <p> {porcentagemTempoPgmOnline}% do tempo online</p>
                            <p>{porcentagemTempoPgmOffline}% do tempo offline</p>
                            <div ref={chartRefPGM} style={{ width: "100%", height: "calc(80vh - 80px)" }} />
                            <div style={{ marginTop: "10px", display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
                                <div style={{ backgroundColor: "#FF0000", width: 20, height: 20 }}></div><span>Offline</span>
                                <div style={{ backgroundColor: "#00B050", width: 20, height: 20 }}></div><span>Online</span>
                            </div>
                        </div>

                        <div style={{ width: "90%", height: "80vh", margin: "20px auto", display: "flex", flexDirection: "column", alignItems: "center", border: "1px solid #ddd", borderRadius: "8px", padding: "10px" }}>
                            <div style={{ textAlign: "center", marginTop: "20px" }}>
                                <h4 style={{ marginBottom: "10px" }}>CENTRAL</h4>
                                <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "12px 24px", maxWidth: "800px", margin: "0 auto" }}>
                                    <p style={{ margin: 0, fontSize: "14px", whiteSpace: "nowrap" }}>{porcentagemTempoArmado}% do tempo <strong>Armada</strong></p>
                                    <p style={{ margin: 0, fontSize: "14px", whiteSpace: "nowrap" }}>{porcentagemTempoDesarmado}% do tempo <strong>Desarmada</strong></p>
                                    <p style={{ margin: 0, fontSize: "14px", whiteSpace: "nowrap" }}>{porcentagemTempoAlarmado}% do tempo <strong>Alarmada</strong></p>
                                    <p style={{ margin: 0, fontSize: "14px", whiteSpace: "nowrap" }}>{porcentagemTempoDesconectado}% do tempo <strong>Desconectado</strong></p>
                                    <p style={{ margin: 0, fontSize: "14px", whiteSpace: "nowrap" }}>{porcentagemTempoIndefinido}% do tempo <strong>Indefinido</strong></p>
                                </div>
                            </div>
                            <div ref={chartRefCentral} style={{ width: "100%", height: "calc(80vh - 120px)" }} />
                            <div style={{ marginTop: "10px", display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
                                <div style={{ backgroundColor: "#FF0000", width: 20, height: 20 }}></div><span>Alarme / Desconectado</span>
                                <div style={{ backgroundColor: "#C0C0C0", width: 20, height: 20 }}></div><span>Desarmada</span>
                                <div style={{ backgroundColor: "#FFA500", width: 20, height: 20 }}></div><span>Indefinido</span>
                                <div style={{ backgroundColor: "#00B050", width: 20, height: 20 }}></div><span>Armada</span>
                            </div>
                        </div>

                        <div style={{ width: "90%", margin: "20px auto", display: "flex", flexDirection: "column", border: "1px solid #ddd", borderRadius: "8px", padding: "20px" }}>
                            <h4 style={{ textAlign: "center", marginBottom: "20px" }}>Análise de Zonas</h4>
                            {rankingZonas && rankingZonas.length > 0 ? (
                                <div>
                                    <h5 style={{ borderBottom: "1px solid #eee", paddingBottom: "10px" }}>Tempo por Estado de Cada Zona</h5>
                                    <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "30px", textAlign: "left", fontSize: "14px" }}>
                                        <thead>
                                            <tr style={{ backgroundColor: "#f0f4f8" }}>
                                                <th style={{ padding: "10px 12px", border: "1px solid #ddd", fontWeight: "bold" }}>ID</th>
                                                <th style={{ padding: "10px 12px", border: "1px solid #ddd", fontWeight: "bold" }}>Zona</th>
                                                <th style={{ padding: "10px 12px", border: "1px solid #ddd", textAlign: "center", color: "#00B050", fontWeight: "bold" }}>🟢 Armada</th>
                                                <th style={{ padding: "10px 12px", border: "1px solid #ddd", textAlign: "center", color: "#888", fontWeight: "bold" }}>⚪ Desarmada</th>
                                                <th style={{ padding: "10px 12px", border: "1px solid #ddd", textAlign: "center", color: "#FF4500", fontWeight: "bold" }}>⚫ Offline</th>
                                                <th style={{ padding: "10px 12px", border: "1px solid #ddd", textAlign: "center", color: "#FF0000", fontWeight: "bold" }}>🔴 Alarmada</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rankingZonas.map((zona, index) => {
                                                const total = (zona.armada + zona.desarmada + zona.offline + zona.alarmada) || 1;
                                                return (
                                                    <tr key={zona.id} style={{ backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa" }}>
                                                        <td style={{ padding: "10px 12px", border: "1px solid #ddd", color: "#555" }}>{zona.id}</td>
                                                        <td style={{ padding: "10px 12px", border: "1px solid #ddd", fontWeight: "600" }}>{zona.nome}</td>
                                                        <td style={{ padding: "10px 12px", border: "1px solid #ddd", textAlign: "center" }}>
                                                            <span style={{ color: "#00B050", fontWeight: "bold" }}>{zona.armada}</span>
                                                            <span style={{ color: "#aaa", fontSize: "12px", marginLeft: "4px" }}>({((zona.armada / total) * 100).toFixed(1)}%)</span>
                                                        </td>
                                                        <td style={{ padding: "10px 12px", border: "1px solid #ddd", textAlign: "center" }}>
                                                            <span style={{ color: "#555", fontWeight: "bold" }}>{zona.desarmada}</span>
                                                            <span style={{ color: "#aaa", fontSize: "12px", marginLeft: "4px" }}>({((zona.desarmada / total) * 100).toFixed(1)}%)</span>
                                                        </td>
                                                        <td style={{ padding: "10px 12px", border: "1px solid #ddd", textAlign: "center" }}>
                                                            <span style={{ color: "#FF4500", fontWeight: "bold" }}>{zona.offline}</span>
                                                            <span style={{ color: "#aaa", fontSize: "12px", marginLeft: "4px" }}>({((zona.offline / total) * 100).toFixed(1)}%)</span>
                                                        </td>
                                                        <td style={{ padding: "10px 12px", border: "1px solid #ddd", textAlign: "center" }}>
                                                            <span style={{ color: "#FF0000", fontWeight: zona.alarmada > 0 ? "bold" : "normal" }}>{zona.alarmada}</span>
                                                            <span style={{ color: "#aaa", fontSize: "12px", marginLeft: "4px" }}>({((zona.alarmada / total) * 100).toFixed(1)}%)</span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>

                                    {rankingZonas.some(z => z.periodos && z.periodos.length > 0) && (
                                        <>
                                            <h5 style={{ borderBottom: "1px solid #eee", paddingBottom: "10px" }}>Faixas de Horário (Períodos de Alarme)</h5>
                                            {rankingZonas.filter(z => z.periodos && z.periodos.length > 0).map((zona) => (
                                                <div key={zona.id} style={{ marginBottom: "20px", padding: "10px", backgroundColor: "#fafafa", borderRadius: "5px" }}>
                                                    <strong style={{ fontSize: "16px" }}>Zona: {zona.nome} (ID {zona.id})</strong>
                                                    <ul style={{ marginTop: "10px", paddingLeft: "20px" }}>
                                                        {zona.periodos.map((p, i) => {
                                                            const formatData = (d) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
                                                            const formatHora = (d) => d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                                                            const inicioStr = `${formatData(p.inicio)} às ${formatHora(p.inicio)}`;
                                                            if (p.inicio.getTime() === p.fim.getTime()) {
                                                                return <li key={i}>Registro isolado em {inicioStr}</li>;
                                                            } else {
                                                                const dataInicio = formatData(p.inicio);
                                                                const dataFim = formatData(p.fim);
                                                                if (dataInicio === dataFim) {
                                                                    return <li key={i}>Dia {dataInicio}: Das {formatHora(p.inicio)} até {formatHora(p.fim)} (Ininterrupto nos registros)</li>;
                                                                } else {
                                                                    return <li key={i}>De {inicioStr} até {`${formatData(p.fim)} às ${formatHora(p.fim)}`} (Ininterrupto nos registros)</li>;
                                                                }
                                                            }
                                                        })}
                                                    </ul>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div style={{ textAlign: "center", color: "#666" }}>
                                    <p>Nenhuma zona encontrada nos registros deste período.</p>
                                    <div style={{fontSize: '10px', wordBreak: 'break-all', textAlign: 'left', background: '#f5f5f5', padding: '10px'}}>
                                        <b>DEBUG - Chaves do primeiro registro retornado pela API:</b><br/>
                                        {registrosGerais && registrosGerais.length > 0 ? JSON.stringify(Object.keys(registrosGerais[0])) : 'Nenhum registro'}
                                    </div>
                                </div>
                            )}

                            <div style={{ width: "100%", marginTop: "30px", marginBottom: "30px", borderTop: "1px solid #eee", paddingTop: "20px" }}>
                                <h5 style={{ textAlign: "center", marginBottom: "15px" }}>Gráfico de Status por Zona</h5>
                                <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
                                    <FormControl variant="outlined" style={{ minWidth: 200 }}>
                                        <InputLabel id="select-zona-label">Selecione a Zona</InputLabel>
                                        <Select
                                            labelId="select-zona-label"
                                            value={zonaSelecionada}
                                            onChange={(e) => setZonaSelecionada(e.target.value)}
                                            label="Selecione a Zona"
                                        >
                                            {rankingZonas && rankingZonas.length > 0 ? (
                                                rankingZonas.map(z => (
                                                    <MenuItem key={z.id} value={z.id}>{z.id} - {z.nome}</MenuItem>
                                                ))
                                            ) : (
                                                <MenuItem value="">Nenhuma zona disponível</MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                </div>
                                {optionsZona ? (
                                    <>
                                        <div ref={chartRefZona} style={{ width: "100%", height: "400px" }} />
                                        <div style={{ marginTop: "10px", display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
                                            <div style={{ backgroundColor: "#FF0000", width: 20, height: 20 }}></div><span>Alarmada</span>
                                            <div style={{ backgroundColor: "#FF4500", width: 20, height: 20 }}></div><span>Offline</span>
                                            <div style={{ backgroundColor: "#C0C0C0", width: 20, height: 20 }}></div><span>Desarmada</span>
                                            <div style={{ backgroundColor: "#00B050", width: 20, height: 20 }}></div><span>Armada</span>
                                        </div>
                                    </>
                                ) : (
                                    <p style={{ textAlign: "center", color: "#666" }}>Carregando gráfico da zona...</p>
                                )}
                            </div>
                        </div>
                    </>
                ) : (
                    <div style={{ textAlign: "center", marginTop: "50px", color: "gray" }}>Nenhum dado de disponibilidade para este período.</div>
                )}
            </div>
        </div>
    );
};

export default GraficoDisponibilidade;
